import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { CalendarIcon, X } from 'lucide-react'
import { useAdvertisementById } from '@/hooks/useAdvertisementById'
import { useAuth } from '@/hooks/useAuth'
import { advertisementService } from '@/services/advertisement_service'
import { UpdateAdvertisement } from '@magnetar/admin-api-interfaces'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useViewMode } from '@/stores/viewMode'

interface BasicInfoProps {
  advertisementId?: number
  onOpenChange: (open: boolean) => void
  onRefresh?: () => void
}

interface FormValues {
  title: string
  description: string
  start_date: Date | null
  end_date: Date | null
  is_active: boolean
}

export function BasicInfo({ advertisementId, onRefresh }: BasicInfoProps) {
  const { token } = useAuth()
  const { id: supplierId } = useViewMode()
  const { advertisement, isLoading, mutate } = useAdvertisementById(supplierId, advertisementId)

  const form = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      start_date: null,
      end_date: null,
      is_active: true,
    },
  })

  useEffect(() => {
    if (advertisement) {
      form.reset({
        title: advertisement.title,
        description: advertisement.description,
        start_date: advertisement.start_date ? new Date(advertisement.start_date) : null,
        end_date: advertisement.end_date ? new Date(advertisement.end_date) : null,
        is_active: advertisement.is_active,
      })
    }
  }, [advertisement, form])

  // 防呆：迄日不可早於起日
  const startDate = form.watch('start_date')

  const onSubmit = async (values: FormValues) => {
    if (!advertisementId || !token || !advertisement) return

    // 後送前再次防呆，避免繞過欄位驗證
    if (values.start_date && values.end_date && values.end_date < values.start_date) {
      toast.error('迄日不可早於起日')
      return
    }

    const data: UpdateAdvertisement = {
      advertisement_id: advertisementId,
      supplier_id: advertisement.supplier_id,
      title: values.title,
      description: values.description,
      start_date: values.start_date ? format(values.start_date, 'yyyy-MM-dd') : null,
      end_date: values.end_date ? format(values.end_date, 'yyyy-MM-dd') : null,
      is_active: values.is_active,
    }

    try {
      const response = await advertisementService.updateAdvertisement(
        supplierId,
        advertisementId,
        data,
        token,
      )

      if (response.success) {
        toast.success('廣告已更新')
        mutate()
        onRefresh?.()
      } else {
        toast.error(response.error?.message || '更新廣告失敗')
      }
    } catch {
      toast.error('更新廣告失敗')
    }
  }

  if (isLoading) {
    return <div className="flex items-center justify-center p-6">載入中...</div>
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h3 className="text-lg font-medium">基本資料</h3>
        <p className="text-sm text-muted-foreground">更新廣告的基本資訊。</p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>廣告標題</FormLabel>
                <FormControl>
                  <Input placeholder="請輸入廣告標題" {...field} />
                </FormControl>
                <FormDescription>這將顯示在廣告列表中。</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>廣告描述</FormLabel>
                <FormControl>
                  <Textarea placeholder="請輸入廣告描述" className="resize-none" {...field} />
                </FormControl>
                <FormDescription>簡短描述這個廣告的用途。</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="start_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>廣告起日</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          'w-full pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground',
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'yyyy/MM/dd', { locale: zhTW })
                        ) : (
                          <span>請選擇廣告起日</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ?? undefined}
                      onSelect={(date) => field.onChange(date ?? null)}
                      locale={zhTW}
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription>設定此廣告的開始日期。</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="end_date"
            rules={{
              validate: (value) =>
                !value || !startDate || value >= startDate || '迄日不可早於起日',
            }}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>廣告迄日</FormLabel>
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'yyyy/MM/dd', { locale: zhTW })
                          ) : (
                            <span>請選擇廣告迄日</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ?? undefined}
                        onSelect={(date) => field.onChange(date ?? null)}
                        disabled={startDate ? { before: startDate } : undefined}
                        locale={zhTW}
                      />
                      {field.value && (
                        <div className="border-t p-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full"
                            onClick={() => field.onChange(null)}
                          >
                            清空迄日
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  {field.value && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      title="清空迄日"
                      aria-label="清空迄日"
                      onClick={() => field.onChange(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <FormDescription>設定此廣告的結束日期。留空表示進行中。</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">啟用狀態</FormLabel>
                  <FormDescription>設定此廣告是否啟用。停用的廣告將不會顯示。</FormDescription>
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
          <Button type="submit">儲存變更</Button>
        </form>
      </Form>
    </div>
  )
}
